import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { trackEvent } from '../utils/analytics';
import SEO from '../components/SEO';

/**
 * /buy/{slug}: the link for Instagram stories and bios, WhatsApp messages,
 * ads and AI assistants. It puts the product in the cart and opens
 * checkout in one step. ?qty=2 sets the quantity; /buy?items=a:2,b:1 adds
 * several products (AI assistants' create_cart_link); campaign parameters
 * (utm_*) carry through to checkout. When the product can't be bought
 * straight away (a colour to choose, out of stock), it opens the product
 * page instead. Link previews come from the server (the product's card).
 */
const BuyLink = () => {
  const { slug } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // StrictMode runs effects twice; add once
    started.current = true;
    const params = new URLSearchParams(search);
    const qty = Math.min(20, Math.max(1, parseInt(params.get('qty'), 10) || 1));
    // /buy?items=slug:2,other:1 (from AI assistants' create_cart_link) or /buy/slug?qty=2
    const wanted = slug
      ? [[slug, qty]]
      : (params.get('items') || '').split(',').map((part) => part.split(':')).filter(([s]) => s)
        .slice(0, 20).map(([s, n]) => [s, Math.min(20, Math.max(1, parseInt(n, 10) || 1))]);
    params.delete('qty');
    params.delete('items');
    const carry = params.toString() ? `?${params.toString()}` : '';
    if (!wanted.length) { setFailed(true); return; }

    Promise.all(wanted.map(([s, n]) => api.get(`/api/products/${encodeURIComponent(s)}`)
      .then(({ data }) => [data, n]).catch(() => null)))
      .then((found) => {
        const products = found.filter(Boolean);
        if (!products.length) { setFailed(true); return; }
        const buyable = products.filter(([product]) => !(Array.isArray(product.variants) && product.variants.some((v) => v.active !== false))
          && product.stockStatus !== 'out_of_stock'
          && !['coming_soon', 'discontinued'].includes(product.availabilityStatus));
        // One product that needs a choice or is unavailable: show its page.
        if (!buyable.length) {
          navigate(`/products/${products[0][0].slug}${carry}`, { replace: true });
          return;
        }
        buyable.forEach(([product, n]) => {
          addToCart(product, n, null, { skipSidebar: true, skipToast: true });
          trackEvent('buy_link_opened', { item_id: product.sku || String(product.id), quantity: n, source: params.get('utm_source') || 'direct' });
        });
        navigate(`/checkout${carry}`, { replace: true });
      });
  }, [slug, search, navigate, addToCart]);

  return (
    <div style={{ maxWidth: 520, margin: '4rem auto', padding: '0 16px', textAlign: 'center' }}>
      <SEO title={t('buyLink.title', 'Checkout')} noIndex />
      {failed ? (
        <>
          <p>{t('buyLink.notFound', "We couldn't find that product.")}</p>
          <Link to="/products">{t('buyLink.browse', 'Browse all products')}</Link>
        </>
      ) : (
        <p role="status" aria-live="polite">{t('buyLink.opening', 'Opening checkout…')}</p>
      )}
    </div>
  );
};

export default BuyLink;
